import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { Carousel, CarouselContent, CarouselIndicator, CarouselItem } from '@lastbrand/web-ui/carousel'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import React, { useMemo } from 'react'

import { RichTextDS } from '@src/components/blocks/rich-text-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import { Image } from '@src/quince-components/src/atoms/image'

import type { TrustBadgeRow, TrustBadgesDSProps } from './trust-badges-ds.types'
import type { Media } from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'

/**
 * DS port of the PL TRUST_BADGES widget. Full-bleed band of icon + uppercase title + caption badges:
 * an equal-width N-column grid on desktop; on mobile either a one-badge-per-slide carousel with dots
 * (the default — see `isMobileCarousel`, driven by the section's `layoutType`) or a centred single
 * column.
 *
 * Tokens come from `trust-badges.module.scss` (legacy root 10px), and the styling mirrors the non-PL
 * twin `text-block-with-icon-row-ds.tsx` (the icon + uppercase title + caption band, test ids
 * `text-block-with-icon-row-ds-desktop` / `-mobile`) so the PL and non-PL renders of the same section
 * are indistinguishable.
 */

const bandClass = css({ paddingBlock: { base: 'space-64', lg: 'space-100' } })

// Legacy `.headerText`: 22px / 500 / 30px lh / 0.06em, centered. There is no `title-22` token, so
// these stay raw rem against the DS 16px root (matches the icon-row header exactly).
const headingClass = css({
  margin: '0',
  marginBottom: { base: 'space-20', lg: 'space-40' },
  color: 'black',
  fontSize: '1.375rem',
  lineHeight: '1.875rem',
  fontWeight: 500,
  letterSpacing: '0.06em',
  textAlign: 'center',
})

// Desktop equal N-column row (`--trust-badges-columns` = runtime badge count), matching the icon
// row's `rowGridClass`; mobile is the centred `stackClass` column at space-32.
const rowClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space-32',
  alignItems: 'center',
  lg: {
    display: 'grid',
    gridTemplateColumns: 'repeat(var(--trust-badges-columns), 1fr)',
    gap: 'space-32',
    alignItems: 'start',
  },
})

// Centred stack: icon + uppercase title + caption. Inter-element spacing is per-element margins (as
// on the icon row), not a container `gap`, so each step can differ per breakpoint.
const badgeClass = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  color: 'black',
})

const iconClass = css({
  display: 'block',
  width: '2.5rem',
  height: '2.5rem',
  objectFit: 'contain',
  marginBottom: { base: 'space-16', lg: 'space-24' },
})

const titleClass = css({
  margin: '0',
  marginBottom: { base: 'space-8', lg: 'space-16' },
  color: 'black',
  textStyle: 'title-14',
})

// `body-14` declares no colour, so this is the caption's only source of one. Both legacy sources say
// black — PL `trust-badges.module.scss:117` (`#21201f`) and non-PL `featureSection.module.scss:95`
// (`$black`) — as does the non-PL DS twin (`text-block-with-icon-row-ds.tsx:88`). The earlier
// `darkgray` here rendered `#424141` and was the one property breaking the twin parity above.
const CAPTION_TYPO_BASE: SystemStyleObject = { textStyle: 'body-14', color: 'black' }

// Legacy caption cap: 240px on desktop, full-width on mobile; centred.
const captionClass = css({ maxWidth: { base: '100%', lg: '240px' }, marginInline: 'auto' })

const indicatorWrapClass = css({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 'space-24',
})

// One badge per slide (icon row's `flexBasis: '100%'` CarouselItem).
const CAROUSEL_ITEM_CSS = { flexBasis: '100%' } as const

function BadgeIcon({ media, className }: Readonly<{ media?: Media; className: string }>) {
  const content = media?.content
  if (!content?.url) return null

  // Protocol-relative proto URL — prepend the scheme.
  const url = content.url.startsWith('//') ? `https:${content.url}` : content.url

  // Proto `type` is the authoritative classifier, so it's checked before the URL-extension sniff.
  if (media?.type === MediaType.VIDEO) {
    return (
      <video className={className} src={url} muted playsInline loop autoPlay aria-label={content.alt ?? undefined} />
    )
  }

  // SVG line icons bypass the raster CDN pipeline.
  if (/\.svg(\?|#|$)/i.test(url)) {
    return (
      <img
        className={className}
        src={url}
        alt={content.alt ?? ''}
        loading="lazy"
        width={content.width ? content.width : undefined}
        height={content.height ? content.height : undefined}
      />
    )
  }

  // `useNative` renders a plain <img> honoring the fixed 40x40 class; the default path builds an
  // aspect-box wrapper that ignores the size and inflates the icon to the column width.
  return (
    <Image
      url={url}
      title={content.alt ?? ''}
      alt={content.alt ?? ''}
      className={className}
      loading="lazy"
      useNative
      width={content.width ? content.width : undefined}
      height={content.height ? content.height : undefined}
    />
  )
}

function TrustBadge({ row }: Readonly<{ row: TrustBadgeRow }>) {
  return (
    <div className={badgeClass}>
      <BadgeIcon media={row.icon} className={iconClass} />
      {row.title ? <span className={titleClass}>{row.title}</span> : null}
      {row.subTitle ? (
        <RichTextDS content={row.subTitle} baseStyle={CAPTION_TYPO_BASE} className={captionClass} />
      ) : null}
    </div>
  )
}

export function TrustBadgesDS({ heading, rows, backgroundColor, testId, isMobileCarousel }: TrustBadgesDSProps) {
  // The legacy Swiper atom ships SwiperJS's Mousewheel module globally (`forceToAxis`), so the
  // Control carousel pages on touchpad/wheel gestures; bare Embla only listens to pointer drag,
  // forcing a click-drag first (STRFRNT-16439). Wheel support only — no `dragFree` (unlike
  // `useFreeDragCarousel`): Control is `freeMode={false}` and a one-badge-per-page dots carousel
  // must settle exactly on a slide, never between snaps. Memoized: a fresh plugins array would
  // re-init Embla every render.
  const carouselPlugins = useMemo(() => [WheelGesturesPlugin()], [])

  if (rows.length === 0) return null

  return (
    <SectionContentContainer
      as="section"
      data-testid={testId}
      className={bandClass}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {heading ? (
        <h2 className={headingClass} data-testid={TEST_LOCATORS.TRUST_BADGES.HEADING}>
          {heading}
        </h2>
      ) : null}
      {isMobileCarousel ? (
        // One badge per slide + dots, mirroring the non-PL icon row's CAROUSEL branch. Only ever
        // rendered on mobile (the widget gates on `useDeviceInfo`, as the legacy PL widget does), so
        // the badges appear once in the DOM rather than as CSS-hidden twin trees.
        <Carousel plugins={carouselPlugins}>
          <CarouselContent>
            {rows.map(row => (
              <CarouselItem key={row.key} css={CAROUSEL_ITEM_CSS}>
                <TrustBadge row={row} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className={indicatorWrapClass}>
            <CarouselIndicator />
          </div>
        </Carousel>
      ) : (
        <div className={rowClass} style={{ '--trust-badges-columns': String(rows.length) } as React.CSSProperties}>
          {rows.map(row => (
            <TrustBadge key={row.key} row={row} />
          ))}
        </div>
      )}
    </SectionContentContainer>
  )
}
